import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { reactEvents } from '@bridge/reactEvents';
import { Role } from '@texas/api/endpoints/userApi';
import { Icons } from '@texas/components/shared/Icons';
import { RoleRestrict } from '@texas/components/shared/RoleRestrict';
import { useTranslation } from 'react-i18next';

interface CompactGlobalUtilityNavigationProps {
  activeState: string;
  hasPendingJobs: boolean;
  tasksCount: number;
}

const compactUtilityNavigationStyles = {
  '@media screen and (max-height: 900px)': {
    display: 'block',
  },
};

const utilityNavigationRoutes = [
  'tags',
  'tasks',
  'jobs',
  'configuration',
  'archive',
];

function isStateActive(activeState: string, route: string) {
  return activeState === route || activeState.startsWith(`${route}.`);
}

export function CompactGlobalUtilityNavigation({
  activeState,
  hasPendingJobs,
  tasksCount,
}: CompactGlobalUtilityNavigationProps) {
  const { t } = useTranslation();
  const isActive = utilityNavigationRoutes.some((route) =>
    isStateActive(activeState, route),
  );

  return (
    <Box
      display="none"
      position="relative"
      w={20}
      pt={3}
      borderTopWidth="1px"
      borderColor="whiteAlpha.200"
      sx={compactUtilityNavigationStyles}
    >
      <Menu placement="right-end">
        <MenuButton
          as={Button}
          background={isActive ? 'texas.burntSienna.500' : 'transparent'}
          color={isActive ? 'white' : 'navigation.globalMuted'}
          sx={{
            '& > span': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              width: '100%',
              height: '100%',
            },
          }}
          variant="globalNavigation"
        >
          <Icons.DotsHorizontal boxSize={5} />
          <Text fontSize="12px" fontWeight="normal" lineHeight="14px">
            {t('general.more')}
            {tasksCount > 0 ? ` · ${tasksCount > 9 ? '9+' : tasksCount}` : ''}
          </Text>
        </MenuButton>
        <Portal>
          <MenuList color="chakra-body-text" minW="180px">
            <MenuItem
              icon={<Icons.Tag boxSize={4} />}
              onClick={() =>
                reactEvents.routingChanged.dispatch({ to: 'tags' })
              }
            >
              {t('navigation.tags')}
            </MenuItem>
            <MenuItem
              icon={<Icons.CalendarCheck boxSize={4} />}
              onClick={() =>
                reactEvents.routingChanged.dispatch({ to: 'tasks' })
              }
            >
              <Flex align="center" justify="space-between" w="full">
                <Text>{t('navigation.tasks')}</Text>
                {tasksCount > 0 && (
                  <Text color="texas.burntSienna.500" fontWeight="bold">
                    {tasksCount > 9 ? '9+' : tasksCount}
                  </Text>
                )}
              </Flex>
            </MenuItem>
            <MenuItem
              icon={
                hasPendingJobs ? (
                  <Spinner boxSize={4} speed="0.7s" thickness="2px" />
                ) : (
                  <Icons.History boxSize={4} />
                )
              }
              onClick={() =>
                reactEvents.routingChanged.dispatch({ to: 'jobs' })
              }
            >
              {t('navigation.jobs')}
            </MenuItem>
            <RoleRestrict
              allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
            >
              <MenuItem
                icon={<Icons.Cog boxSize={4} />}
                onClick={() =>
                  reactEvents.routingChanged.dispatch({
                    to: 'configuration',
                  })
                }
              >
                {t('navigation.configurationShort')}
              </MenuItem>
            </RoleRestrict>
            <RoleRestrict allow={[Role.SystemAdministrator]}>
              <MenuItem
                icon={<Icons.Archive boxSize={4} />}
                onClick={() =>
                  reactEvents.routingChanged.dispatch({ to: 'archive' })
                }
              >
                {t('navigation.archive')}
              </MenuItem>
            </RoleRestrict>
          </MenuList>
        </Portal>
      </Menu>
    </Box>
  );
}
